/**
 * Warunki, które musi spełnić dobrany układ — jedna lista dla dwóch widoków.
 *
 * Panel w asystencie pokazuje je skrótowo (wartość i zielona fajka), okno
 * VoltValue Projekt rozwija każdy na oś z granicami. Gdyby każdy z nich liczył
 * je po swojemu, po pierwszej poprawce jeden pokazywałby zielone, a drugi
 * czerwone dla tego samego układu.
 *
 * `dolna`/`gorna` mogą być puste — wtedy warunek jest jednostronny (napięcie
 * maksymalne nie ma dolnej granicy, każda niższa wartość jest dobra).
 */
import { DCAC_NISKI, DCAC_WYSOKI } from './flagiOferty'

/**
 * Po czym poznajemy ostrzeżenie o DC/AC.
 *
 * Pokazujemy je w dymku PRZY SAMYM WARUNKU, a nie jako osobne pudełko pod
 * schematem — wartość i tak stoi w tabelce z pomarańczowym trójkątem, więc
 * drugi komunikat o tym samym tylko powtarzał to zdanie obok. Treść bierzemy
 * z backendu (`electrical/stringi.py`), nie przepisujemy jej tutaj: dwie kopie
 * tego samego zdania rozjechałyby się przy pierwszej korekcie progów.
 *
 * Zmiana tego prefiksu po stronie backendu wymaga zmiany TUTAJ — inaczej
 * ostrzeżenie wróci do listy ogólnej i zdubluje dymek.
 */
export const PREFIKS_OSTRZEZENIA_DCAC = 'Przewymiarowanie DC/AC'

/**
 * Ostrzeżenia o prądzie wejścia — też trafiają do dymka przy swoim warunku.
 *
 * Backend przestał przerywać obliczenia przy przekroczonym prądzie (dotyczyło
 * to 17% par moduł–falownik, a użytkownik asystenta nie dobiera modułów po
 * prądzie). Zamiast pustego ekranu dostaje projekt i wyjaśnienie — a to
 * wyjaśnienie ma stać przy trójkącie, nie w kolejnym pudełku pod schematem.
 */
const PREFIKSY_OSTRZEZENIA_PRADU = [
  'Prąd modułu',            // roboczy ponad limit MPPT — ucięcie w szczycie
  'Prąd zwarciowy modułu',  // ponad wytrzymałość wejścia — granica bezpieczeństwa
]

export const jestOstrzezeniemDcAc = (tekst) =>
  typeof tekst === 'string' && tekst.startsWith(PREFIKS_OSTRZEZENIA_DCAC)

export const jestOstrzezeniemPradu = (tekst) =>
  typeof tekst === 'string' && PREFIKSY_OSTRZEZENIA_PRADU.some((p) => tekst.startsWith(p))

/** Ostrzeżenia pokazywane w dymkach przy warunkach — nie dublujemy ich niżej. */
export const jestOstrzezeniemPrzyWarunku = (tekst) =>
  jestOstrzezeniemDcAc(tekst) || jestOstrzezeniemPradu(tekst)

export const warunkiDoboru = (dane) => {
  const z = dane?.zalecany
  const inv = dane?.inverter
  if (!z || !inv) return []

  const lista = [
    {
      klucz: 'napiecie_mroz',
      etykieta: 'Napięcie łańcucha przy mrozie',
      krotka: 'Napięcie w mrozie',
      wartosc: z.napiecie_max_zimno_v,
      gorna: inv.dc_max_voltage_v,
      jednostka: 'V',
    },
    {
      klucz: 'okno_mppt',
      etykieta: 'Napięcie robocze w oknie MPPT',
      krotka: 'Napięcie robocze',
      wartosc: z.napiecie_mpp_v,
      dolna: inv.mppt_v_min,
      gorna: inv.mppt_v_max,
      jednostka: 'V',
    },
    {
      klucz: 'liczba_modulow',
      etykieta: 'Modułów w łańcuchu',
      krotka: 'Modułów w łańcuchu',
      wartosc: z.modulow_w_stringu,
      dolna: dane.n_min,
      gorna: dane.n_max,
      jednostka: 'szt.',
    },
    {
      klucz: 'dcac',
      etykieta: 'Przewymiarowanie DC/AC',
      krotka: 'Przewymiarowanie DC/AC',
      wartosc: z.dcac ?? 0,
      // Te same progi co flagi raportu oferty i ostrzeżenia backendu (0,8–1,5,
      // decyzja właściciela 2026-09-30; wcześniej 1,0–1,3).
      dolna: DCAC_NISKI,
      gorna: DCAC_WYSOKI,
      format: (v) => v.toFixed(2).replace('.', ','),
    },
  ]

  /**
   * Prąd na wejściu falownika.
   *
   * Moduły w łańcuchu idą SZEREGOWO, więc jeden łańcuch niesie prąd pojedynczego
   * modułu — dopiero wpięcie dwóch łańcuchów do tego samego wejścia sumuje prądy
   * i wtedy warunek potrafi się nie spiąć. Dlatego patrzymy na prąd NA WEJŚCIU,
   * a nie na prąd modułu.
   *
   * Dokładamy go tylko, gdy znamy granicę falownika: oś bez limitu udawałaby
   * sprawdzenie, którego nie ma.
   */
  if (inv.prad_max_a && z.prad_na_mppt_a != null) {
    lista.push({
      klucz: 'prad',
      etykieta: inv.mppt_count > 1 ? 'Prąd na wejściu MPPT' : 'Prąd na wejściu DC',
      krotka: inv.mppt_count > 1 ? 'Prąd na wejściu MPPT' : 'Prąd na wejściu DC',
      wartosc: z.prad_na_mppt_a,
      gorna: Math.round(inv.prad_max_a * 10) / 10,
      jednostka: 'A',
    })
  }

  // Wyjaśnienie „dlaczego to źle" trafia do dymka przy SWOIM warunku. Pozostałe
  // warunki mówią same za siebie („napięcie w mrozie poza zakresem" nie wymaga
  // akapitu), a pusty dymek byłby gorszy niż jego brak.
  const dymki = {
    dcac: (dane.ostrzezenia || []).find(jestOstrzezeniemDcAc) || null,
    prad: (dane.ostrzezenia || []).find(jestOstrzezeniemPradu) || null,
  }

  return lista.map((w) => ({
    ...w,
    podpowiedz: dymki[w.klucz] || null,
    spelniony: (w.dolna == null || w.wartosc >= w.dolna)
      && (w.gorna == null || w.wartosc <= w.gorna),
  }))
}

/** Tekst wartości z jednostką — ten sam format w panelu i w oknie. */
export const wartoscWarunku = (w) => (
  w.format ? w.format(w.wartosc) : `${w.wartosc}${w.jednostka ? ` ${w.jednostka}` : ''}`
)

export default warunkiDoboru
